import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { GraphResourceRegistry } from './graph-resource-registry';

/**
 * Main data service to access Office 365 data through Microsoft Graph and graph proxy.
 * Uses HttpBackend handler to avoid having Angular intercept and attempt to authenticate the
 * service requests.
 */
@Injectable({
  providedIn: 'root'
})
export class GraphClientService {
  private http: HttpClient;
  private baseUrl: string;
  private registry: GraphResourceRegistry;

  constructor(
    handler: HttpBackend,
    @Inject('BASE_URL') baseUrl: string,
    registry: GraphResourceRegistry) {
      // This prevents Angular from intercepting the HTTP calls.
      this.http = new HttpClient(handler);
      this.registry = registry;
      this.baseUrl = baseUrl;
  }

  /**
   * Makes a GET REST query to datasource endpoint and returns its data.
   * @param key - a string key to idenfity the query to use.
   * Typically this is a class name of the calling component.
   * @param accessToken - an optional access token. It is used when making calls
   * directly to Microsoft graph API, bypassing the Graph Proxy service.
   */
  public getData(key: string, accessToken?: string): Observable<Object> {
    return this.getDataImpl(key, null, null, accessToken);
  }

  public postData(key: string, body: any, accessToken?: string): Observable<Object> {
    return this.postDataImpl(key, body, null, null, accessToken);
  }

  /**
   * Makes a GET REST query to datasource endpoint and returns its data.
   * @param key - a string key to idenfity the query to use.
   * @param headers - a collection of HTTP headers to pass with the call.
   * @param args - an array of arguments to pass to the REST query.
   * @param accessToken - an optional access token. It is used when making calls
   * directly to Microsoft graph API, bypassing the Graph Proxy service.
   */
  public getDataWithParameters(
    key: string,
    headers: {[header: string]: string},
    args: any[],
    accessToken?: string): Observable<Object> {
    return this.getDataImpl(key, headers, args, accessToken);
  }

  /**
   * Implementation of the call to underlying RESTful data source to get data.
   * @param key - a string key to idenfity the query to use.
   * @param headers - a collection of HTTP headers to pass with the call.
   * @param args - an array of arguments to pass to the REST query.
   * @param accessToken - an optional access token. It is used when making calls
   * directly to Microsoft graph API, bypassing the Graph Proxy service.
   */
  protected getDataImpl(
    key: string,
    headers: {[header: string]: string},
    args: any[],
    accessToken?: string): Observable<Object> {
    const url = (args && args.length > 0) ?
      this.registry.getUrl(key, args) : this.registry.getUrl(key);

    if (!url) {
      return Observable.of({});
    }

    const fullUrl = (url.startsWith('https://')) ? url : this.baseUrl + url;
    const requestHeaders = (headers) ? headers : {};

    if (accessToken) {
      requestHeaders['Authorization'] = 'Bearer ' + accessToken;
    }

    return this.http.get(fullUrl, { headers: requestHeaders});
  }

  protected postDataImpl(
    key: string,
    body: any,
    headers: { [header: string]: string },
    args: any[],
    accessToken?: string): Observable<Object> {
    const url = (args && args.length > 0) ?
      this.registry.getUrl(key, args) : this.registry.getUrl(key);

    if (!url) {
      return Observable.of({});
    }

    const fullUrl = (url.startsWith('https://')) ? url : this.baseUrl + url;
    const requestHeaders = (headers) ? headers : {};

    if (accessToken) {
      requestHeaders['Authorization'] = 'Bearer ' + accessToken;
    }

    return this.http.post(fullUrl, body, { headers: requestHeaders });
  }
}
